import is from '@sindresorhus/is';
import { z } from 'zod';

import type { ProfileTaskParentContext } from '../model/profile-task';

/** Tile payloads serialise into dot-delimited url segments, so an id may not carry a period. */
export const segmentIdSchema = z
    .string()
    .trim()
    .min(1)
    .refine((value) => !value.includes('.'), { message: 'Tile identifiers may not contain a period' });

/**
 * Where an automation tile's task lives. Opened from a job profile it addresses the task like the task
 * tile does; opened from a work task, which belongs to no profile, the parent fields are absent.
 */
export const optionalProfileAddressShape = {
    architectureId: z.uuid().optional(),
    parentId: z.uuid().optional(),
    parentType: z.enum(['level', 'role']).optional(),
    parentVersion: z.number().int().positive().optional(),
    roleId: z.uuid().optional(),
};

type OptionalProfileAddress = {
    readonly architectureId?: string | undefined;
    readonly parentId?: string | undefined;
    readonly parentType?: ProfileTaskParentContext['parentType'] | undefined;
    readonly parentVersion?: number | undefined;
    readonly roleId?: string | undefined;
};

export const isWholeOrNoProfileAddress = ({ architectureId, parentId, parentType }: OptionalProfileAddress): boolean =>
    [architectureId, parentId, parentType].every(is.undefined) ||
    ![architectureId, parentId, parentType].some(is.undefined);

export const profileAddressMessage = 'A job profile address needs its architecture, parent type and parent together';

export const toProfileAddress = (context: ProfileTaskParentContext | undefined): OptionalProfileAddress =>
    is.undefined(context)
        ? {}
        : {
              architectureId: context.architectureId,
              parentId: context.parentId,
              parentType: context.parentType,
              ...(is.undefined(context.parentVersion) ? {} : { parentVersion: context.parentVersion }),
              ...(is.undefined(context.roleId) ? {} : { roleId: context.roleId }),
          };

export const toProfileContext = ({
    architectureId,
    parentId,
    parentType,
    parentVersion,
    roleId,
}: OptionalProfileAddress): ProfileTaskParentContext | undefined =>
    architectureId === undefined || parentId === undefined || parentType === undefined
        ? undefined
        : { architectureId, parentId, parentType, parentVersion, roleId };
